import { supabase } from '../lib/supabase';

// Action constants for consistent logging
export const AUDIT_ACTIONS = {
  // User & Role actions
  USER_ROLE_CHANGED: 'user_role_changed',
  USER_ACTIVATED: 'user_activated',
  USER_DEACTIVATED: 'user_deactivated',
  USER_ADDED_AS_ADMIN: 'user_added_as_admin',
  PERMISSION_SAVED: 'permission_saved',

  // Estimate actions
  ESTIMATE_CREATED: 'estimate_created',
  ESTIMATE_UPDATED: 'estimate_updated',
  ESTIMATE_DELETED: 'estimate_deleted',
  ESTIMATE_EXPORTED: 'estimate_exported',

  // Award actions
  AWARD_ISSUED: 'award_issued',
  VENDOR_SELECTED: 'vendor_selected',
  VENDOR_DESELECTED: 'vendor_deselected',

  // Approval actions
  APPROVAL_GRANTED: 'approval_granted',
  APPROVAL_REVOKED: 'approval_revoked',

  // Master data actions
  MASTER_DATA_CREATED: 'master_data_created',
  MASTER_DATA_UPDATED: 'master_data_updated',
  MASTER_DATA_DELETED: 'master_data_deleted',

  // Incentive Ticket actions
  INCENTIVE_TICKET_DELETED: 'incentive_ticket_deleted',
  INCENTIVE_TICKET_STATUS_CHANGED: 'incentive_ticket_status_changed',
};

// Entity type constants
export const AUDIT_ENTITIES = {
  USER: 'user',
  ROLE: 'role',
  PERMISSION: 'permission',
  ESTIMATE: 'estimate',
  AWARD: 'award',
  VENDOR: 'vendor',
  MASTER_DATA: 'master_data',
  INCENTIVE_TICKET: 'incentive_ticket',
};

/**
 * Log a critical action to the audit_logs table.
 * Fire-and-forget: errors are silently caught to avoid disrupting the main flow.
 *
 * @param {Object} params
 * @param {Object} params.user - The current user object from AuthContext
 * @param {Object} params.userProfile - The current userProfile from AuthContext
 * @param {string} params.action - One of AUDIT_ACTIONS
 * @param {string} params.entityType - One of AUDIT_ENTITIES
 * @param {string} [params.entityId] - ID of the affected entity
 * @param {string} [params.entityName] - Human-readable name of the entity
 * @param {Object} [params.oldValue] - Previous state (for updates)
 * @param {Object} [params.newValue] - New state (for updates/creates)
 * @param {Object} [params.metadata] - Any extra context
 */
export const logAuditEvent = async ({
  user,
  userProfile,
  action,
  entityType,
  entityId = null,
  entityName = null,
  oldValue = null,
  newValue = null,
  metadata = null,
}) => {
  try {
    if (!user?.id) return;

    await supabase?.from('audit_logs')?.insert({
      user_id: user?.id,
      user_email: user?.email || userProfile?.email || null,
      user_role: userProfile?.role || null,
      action,
      entity_type: entityType,
      entity_id: entityId ? String(entityId) : null,
      entity_name: entityName || null,
      old_value: oldValue || null,
      new_value: newValue || null,
      metadata: metadata || null,
      created_at: new Date()?.toISOString(),
    });
  } catch (err) {
    // Audit logging must never break the main flow
    console.warn('[AuditLog] Failed to log event:', err?.message);
  }
};

/**
 * Fetch audit logs (admin only).
 * @param {Object} options
 * @param {number} [options.limit=100]
 * @param {number} [options.offset=0]
 * @param {string} [options.entityType] - Filter by entity type
 * @param {string} [options.action] - Filter by action
 * @param {string} [options.userId] - Filter by user
 */
export const getAuditLogs = async ({ limit = 100, offset = 0, entityType = null, action = null, userId = null } = {}) => {
  let query = supabase
    ?.from('audit_logs')
    ?.select('*')
    ?.order('created_at', { ascending: false })
    ?.range(offset, offset + limit - 1);

  if (entityType) query = query?.eq('entity_type', entityType);
  if (action) query = query?.eq('action', action);
  if (userId) query = query?.eq('user_id', userId);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/**
 * Get total count of audit logs (for pagination).
 */
export const getAuditLogsCount = async ({ entityType = null, action = null, userId = null } = {}) => {
  let query = supabase
    ?.from('audit_logs')
    ?.select('*', { count: 'exact', head: true });

  if (entityType) query = query?.eq('entity_type', entityType);
  if (action) query = query?.eq('action', action);
  if (userId) query = query?.eq('user_id', userId);

  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
};
